// Widgets
import { createAction, createReducer } from "@reduxjs/toolkit"
import _ from "lodash"
import moment from "moment"
import httpClient from "./httpClient"
import { checkUserType } from "../component/userUtils"
import { STAFF } from "../commons/userTypes"

/**
 * Define all selector IDs
 */
export const AM_MANAGEMENT_STYLE_SELECTOR_ID = "am_management_style_selector"
export const AM_MASTER_FUND_SELECTOR_ID = "am_master_fund_selector"
export const AM_FUND_SHARE_SELECTOR_ID = "am_fund_share_selector"

export const SYNTHESIS_END_DATE_SELECTOR_ID = "synthesis_end_date_selector"
export const WEALTH_PORTFOLIO_SELECTOR_ID = "wealth_portfolio_selector"
export const WEALTH_CUSTOMER_SELECTOR_ID = "wealth_customer_selector"
export const WEALTH_START_DATE_SELECTOR_ID = "wealth_start_date_picker"
export const WEALTH_END_DATE_SELECTOR_ID = "wealth_end_date_picker"
export const WEALTH_END_MONTH_DATE_SELECTOR_ID = "wealth_end_month_picker"
export const WEALTH_YEAR_SELECTOR_ID = "wealth_year_picker"

/**
 * Map a selector ID with a REST API route to get available values
 */
const selectors = {
  [AM_MANAGEMENT_STYLE_SELECTOR_ID]: "/widgets/managementstyle/selector",
  [AM_MASTER_FUND_SELECTOR_ID]: "/widgets/funds/selector",
  [AM_FUND_SHARE_SELECTOR_ID]: "/widgets/fundshares/selector",
  [WEALTH_CUSTOMER_SELECTOR_ID]: "/widgets/customer/selector",
  [WEALTH_PORTFOLIO_SELECTOR_ID]: "/widgets/asset/selector"
}

/**
 * Define initial state for each selector
 */
const initialStateSelector = {
  [AM_MANAGEMENT_STYLE_SELECTOR_ID]: undefined,
  [WEALTH_PORTFOLIO_SELECTOR_ID]: undefined,
  [WEALTH_CUSTOMER_SELECTOR_ID]: undefined,
  [WEALTH_START_DATE_SELECTOR_ID]: { selected: moment().subtract(1, "year") },
  [WEALTH_END_DATE_SELECTOR_ID]: { selected: moment() },
  [SYNTHESIS_END_DATE_SELECTOR_ID]: { selected: moment() },
  [WEALTH_END_MONTH_DATE_SELECTOR_ID]: { selected: moment() },
  [WEALTH_YEAR_SELECTOR_ID]: { selected: moment() }
}

/**
 * Reducer initialState
 * @type
 */
const initialState = {
  list: [],
  selector: initialStateSelector,
  widgetCallsInProgress: [],
  widgetCallsWithErrors: 0,
  previewEnabled: false,
  messages: []
}

const WIDGET_SELECTOR = createAction("WIDGET_SELECTOR")
const WIDGET_SELECTOR_VALUE = createAction("WIDGET_SELECTOR_VALUE")
const WIDGET_SELECTOR_CLEAR = createAction("WIDGET_SELECTOR_CLEAR")

const GET_WIDGETS_RESPONSE = createAction("GET_WIDGETS_RESPONSE")
const GET_WIDGET_RESPONSE = createAction("GET_WIDGET_RESPONSE")
const ENABLE_WIDGET_TEMPLATE = createAction("ENABLE_WIDGET_TEMPLATE")
const ADD_WIDGET_MESSAGE = createAction("ADD_WIDGET_MESSAGE")
const CLEAR_WIDGET_MESSAGE = createAction("CLEAR_WIDGET_MESSAGE")

// To follow loading of widgets
const WIDGET_CALL_ADD = createAction("WIDGET_CALL_ADD")
const WIDGET_CALL_REMOVE = createAction("WIDGET_CALL_REMOVE")
const WIDGET_CALL_ERROR = createAction("WIDGET_CALL_ERROR")
const WIDGET_CALL_RESET = createAction("WIDGET_CALL_RESET")

function fetchWidgets(cancelToken) {
  return (dispatch) =>
    httpClient
      .get("/widgets", { cancelToken })
      .then(({ data }) => dispatch(GET_WIDGETS_RESPONSE(data)))
}

function fetchWidgetDataInner(args) {
  return (dispatch, getState) => {
    const state = getState()
    const locale = state?.intl?.locale || state?.intl?.defaultLocale || "fr"
    const { endpoint, subscriptionsParams, token } = args
    const body = { ...subscriptionsParams, locale }
    return httpClient
      .post(endpoint, body, { cancelToken: token })
      .then(({ data }) => data)
  }
}

function fetchWidgetDataConnected(args) {
  return (dispatch) => {
    const { token } = args

    dispatch(WIDGET_CALL_ADD(token))
    return dispatch(fetchWidgetDataInner(args))
      .then((data) => {
        dispatch(GET_WIDGET_RESPONSE(data))
        dispatch(WIDGET_CALL_REMOVE(token))
        return data
      })
      .catch((err) => {
        dispatch(WIDGET_CALL_REMOVE(token))
        dispatch(WIDGET_CALL_ERROR())
        throw err
      })
  }
}

function fetchWidgetData(args) {
  const { disableDispatch } = args
  const fetch = disableDispatch
    ? fetchWidgetDataInner
    : fetchWidgetDataConnected
  return fetch(args)
}

function save(widget, refresh = true) {
  return (dispatch) => {
    return httpClient
      .post(`/widgets/save/${widget.id || ""}`, widget)
      .then(() => {
        if (refresh && widget.id) {
          dispatch(fetchWidgetData(widget._id))
        }
        dispatch(fetchWidgets())
      })
  }
}

function deleteWidgets(widgets) {
  return (dispatch) =>
    httpClient
      .post("/widgets/deleteMany", widgets)
      .finally(() => dispatch(fetchWidgets()))
}

function updateSelector(id, selector) {
  return (dispatch) => dispatch(WIDGET_SELECTOR({ id, selector }))
}

function updateSelectorSelection(id, value) {
  return (dispatch) => dispatch(WIDGET_SELECTOR_VALUE({ id, value }))
}

function clear() {
  return WIDGET_SELECTOR_CLEAR()
}

function enablePreview(isPreview) {
  return ENABLE_WIDGET_TEMPLATE(isPreview)
}

function resetCall() {
  return WIDGET_CALL_RESET()
}

function removeCallOf(token) {
  return WIDGET_CALL_REMOVE(token)
}

/**
 * Get selector by id.
 * Selector is a data structure like:
 * <pre>
 *   {
 *      selected: <String>, or Object of { key, text, value }
 *      values: Array Of String, or Object of { key, text, value }
 *    }
 * </pre>
 * @param id
 * @returns {function(*): *}
 */
function selectorOf(id) {
  return (state) => state.widgets.selector[id]
}

/**
 * Get current selected value of selector ID
 * @param id : selector ID
 * @returns {function(*): *}
 */
function selectedValueOf(id) {
  return (state) => state.widgets.selector[id]?.selected
}

/**
 * Get values from API route for connected selector.
 * @param id
 * @returns {function(*): Promise<AxiosResponse<any>>}
 */
function fetchSelectorValues(id, cancelToken, groupByFn, defaultValue) {
  const endpoint = selectors[id]
  if (!endpoint) {
    throw new Error(
      `unable to fetch selector values for ConnectedSelector ID = ${id}`
    )
  }

  // default function will compute only one undefined group
  // voluntary set undefined... don't panic !
  const groupByFnOrDefault =
    groupByFn || ((values) => _.groupBy(values, "undefined"))

  return (dispatch) => {
    return httpClient.get(endpoint, { cancelToken }).then(({ data }) => {
      const values = data

      // apply a group by portfolio closed
      const groups = groupByFnOrDefault(values)

      // defines selected & values according if a group exists
      let selected
      let sortValues
      if (Object.keys(groups).length <= 1) {
        // handle only one group
        sortValues = _.sortBy(values, "text")
        const foundFirst = _.first(values)
        selected = defaultValue || (foundFirst && foundFirst.value)
      } else if (groupByFn) {
        sortValues = _.sortBy(values, "text")
        const foundFirst = _.first(values)
        selected = defaultValue || (foundFirst && foundFirst.value)
      } else {
        // handle two groups
        // get only non closed portfolios, and select first one
        const allNonClosedPortfolios = groups.false
        const naturalSortPortfolios = _.sortBy(allNonClosedPortfolios, "text")
        const foundFirst = _.first(naturalSortPortfolios)
        selected = defaultValue || (foundFirst && foundFirst.value)
        sortValues = _.sortBy(values, "text")
      }

      const selector = { selected, values: sortValues }
      if (!_.isEmpty(values)) {
        dispatch(updateSelector(id, selector))
      }
      return sortValues
    })
  }
}

/**
 * Non preview view is allowed only for Admin user, otherwise preview mode is always enabled
 * Template preview is a facilitate tool to configure widget on Template page.
 * @returns {function(...[*]=)}
 */
function previewEnabled() {
  return (state) => {
    const authUser = state?.auth?.user
    if (!checkUserType(authUser, STAFF)) {
      return true
    }
    return state.widgets.previewEnabled
  }
}

function addWidgetMessage(message) {
  return (dispatch, getState) => {
    const currSessionUser = getState().auth.user
    if (!checkUserType(currSessionUser, STAFF)) {
      const { messages } = getState().widgets
      const newMessages = [...messages, message]
      dispatch(ADD_WIDGET_MESSAGE(newMessages))
    }
  }
}

function clearWidgetMessage() {
  return (dispatch) => {
    dispatch(CLEAR_WIDGET_MESSAGE())
  }
}

function clearWealthSelector() {
  return (dispatch, getState) => {
    const currSelectorState = getState().widgets.selector
    const newState = _.cloneDeep(currSelectorState)
    newState[WEALTH_START_DATE_SELECTOR_ID] =
      initialStateSelector[WEALTH_START_DATE_SELECTOR_ID]
    newState[WEALTH_END_DATE_SELECTOR_ID] =
      initialStateSelector[WEALTH_END_DATE_SELECTOR_ID]
    newState[SYNTHESIS_END_DATE_SELECTOR_ID] =
      initialStateSelector[SYNTHESIS_END_DATE_SELECTOR_ID]
    newState[WEALTH_END_MONTH_DATE_SELECTOR_ID] =
      initialStateSelector[WEALTH_END_MONTH_DATE_SELECTOR_ID]
    newState[WEALTH_YEAR_SELECTOR_ID] =
      initialStateSelector[WEALTH_YEAR_SELECTOR_ID]
    newState[WEALTH_CUSTOMER_SELECTOR_ID] =
      initialStateSelector[WEALTH_CUSTOMER_SELECTOR_ID]
    newState[WEALTH_PORTFOLIO_SELECTOR_ID] =
      initialStateSelector[WEALTH_PORTFOLIO_SELECTOR_ID]
    dispatch(WIDGET_SELECTOR_CLEAR(newState))
  }
}

const reducer = createReducer(initialState, {
  // warning, one level in initialState
  // current reducer state corresponds to local reducer state
  GET_WIDGETS_RESPONSE: (state, action) => ({
    ...state,
    list: _.map(action.payload, (widget) =>
      _.assign(widget, {
        defaultParams: JSON.stringify(widget.defaultParams, null, "\t")
      })
    )
  }),
  ENABLE_WIDGET_TEMPLATE: (state, { payload }) => ({
    ...state,
    previewEnabled: payload
  }),
  WIDGET_SELECTOR: (state, { payload }) => ({
    ...state,
    selector: {
      ...state.selector,
      [payload.id]: payload.selector
    }
  }),
  WIDGET_SELECTOR_VALUE: (state, { payload }) => ({
    ...state,
    selector: {
      ...state.selector,
      [payload.id]: {
        ...state.selector[payload.id],
        selected: payload.value
      }
    }
  }),
  WIDGET_SELECTOR_CLEAR: (state, { payload }) => ({
    ...state,
    selector: payload
  }),
  WIDGET_CALL_ADD: (state, action) => ({
    ...state,
    widgetCallsInProgress: _.concat(state.widgetCallsInProgress, [
      action.payload
    ])
  }),
  WIDGET_CALL_REMOVE: (state, action) => ({
    ...state,
    widgetCallsInProgress: _.without(
      state.widgetCallsInProgress,
      action.payload
    )
  }),
  WIDGET_CALL_ERROR: (state) => ({
    ...state,
    widgetCallsWithErrors: state.widgetCallsWithErrors + 1
  }),
  WIDGET_CALL_RESET: (state) => ({ ...state, widgetCallsWithErrors: 0 }),
  ADD_WIDGET_MESSAGE: (state, { payload }) => ({ ...state, messages: payload }),
  CLEAR_WIDGET_MESSAGE: (state) => ({
    ...state,
    messages: initialState.messages
  }),
  // FIXME SR : refactor redux...
  AUTH_USER_RESPONSE: (state) => {
    if (!state.loggedIn) {
      // return {...state, selector: }
    }
    return state
  }
})

export default {
  initialState,
  actions: {
    fetchWidgets,
    fetchWidgetData,
    addWidgetMessage,
    clearWidgetMessage,
    clearWealthSelector,
    save,
    deleteWidgets,
    updateSelector,
    updateSelectorSelection,
    clear,
    enablePreview,
    resetCall,
    removeCallOf,
    fetchSelectorValues
  },
  select: { selectorOf, selectedValueOf, previewEnabled },
  reducer
}
